import { useApolloClient } from "@apollo/client";
import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  Form,
  Formik,
  useFormikContext,
  validateYupSchema,
  yupToFormErrors,
} from "formik";
import React from "react";
import { useUserData } from "../../../auth/useUserData.js";
import { useGoogleMapsApi } from "../../../google-maps/useGoogleMapsApi.js";
import { useOnboardingFlowModal_UpdateOrganisationMutation } from "./OnboardingFlowModal.generated.js";
import type {
  OnboardingFlowStage,
  OnboardingViewer,
} from "./OnboardingFlowModal.js";
import { focusFirstInvalidField } from "./focusFirstInvalidField.js";
import { importImageFromUrl } from "../../attachments/helpers.js";
import { OnboardingBusinessFormFields } from "./OnboardingBusinessFormFields.js";
import { OnboardingPropertyFormFields } from "./OnboardingPropertyFormFields.js";
import type { OnboardingSuggestions } from "./OnboardingSuggestionFrame.js";
import {
  OnboardingFlowWebsitePrefill,
  type OnboardingPrefillResult,
} from "./OnboardingFlowWebsitePrefill.js";
import { OnboardingStageLayout } from "./OnboardingStageLayout.js";
import {
  useOnboardingStepLeave,
  type OnboardingStepLeave,
} from "./onboardingStepLeave.js";
import {
  getSuggestedFields,
  getSuggestionStates,
  mergePrefillIntoFormValues,
  removeEditedSuggestions,
  restoreSuggestedField,
  restoreUserInput,
  trackPrefilledValues,
  undoPrefill,
  type OnboardingFormValues,
  type SuggestedField,
} from "./mergePrefillIntoFormValues.js";
import { getErrorPaths, pickErrors } from "./onboardingErrorVisibility.js";
import { createOnboardingFormSchema } from "./onboardingFormSchema.js";
import { getInitialOnboardingEmail } from "./onboardingInitialEmail.js";
import { getDefaultOnboardingPhoneType } from "./onboardingPhoneTypes.js";
import { getPropertyNamePrefill } from "./onboardingPropertyName.js";
import {
  lookupFoundTaxValue,
  shouldShowTaxSection,
} from "./onboardingTaxSection.js";
import {
  buildOrganisationUpdateInput,
  type OnboardingFormVariant,
} from "./onboardingUpdateInput.js";

export type OnboardingOrganisation = OnboardingViewer["organisation"];

// A found logo that takes longer than this to import is given up.
const LOGO_IMPORT_TIMEOUT_MS = 20_000;

// Where a new organisation's address starts; it can be changed in the form.
const DEFAULT_COUNTRY_CODE = "DE";

// The `Email` scalar is untyped (`any`) in the generated query type.
const hasEmail = (email: unknown): email is string =>
  typeof email === "string" && email.trim() !== "";

/**
 * What the step starts from. The legal name starts from the name the
 * organisation was created with: the user's input of the registration. The
 * contact email of a property owner starts from the address the user signed up
 * with; a business starts without one (see `getInitialOnboardingEmail`). Without a
 * stored phone the step starts from an empty one, a landline for a business and
 * a mobile for a property owner. A property owner's property name starts from
 * the organisation title only when the user named it (see
 * `getPropertyNamePrefill`). Defaults stay editable and are only saved on
 * CONTINUE.
 */
const buildInitialValues = (
  organisation: OnboardingOrganisation,
  signedUpEmail: string | null,
  variant: OnboardingFormVariant,
  person: { firstname: string | undefined; familyname: string | undefined }
): OnboardingFormValues => ({
  website: organisation.website ?? null,
  fullLegalName: organisation.fullLegalName?.trim() || organisation.title,
  address: organisation.billingAddress
    ? {
        streetLines1: organisation.billingAddress.streetLines1,
        postalCode: organisation.billingAddress.postalCode,
        city: organisation.billingAddress.city,
        countryCode: organisation.billingAddress.countryCode,
        // A stored address with coordinates counts as confirmed.
        location: organisation.billingAddress.location
          ? {
              lat: organisation.billingAddress.location.lat,
              lng: organisation.billingAddress.location.lng,
            }
          : null,
      }
    : {
        streetLines1: "",
        postalCode: "",
        city: "",
        countryCode: DEFAULT_COUNTRY_CODE,
      },
  email: getInitialOnboardingEmail({
    variant,
    storedEmail: hasEmail(organisation.email) ? organisation.email : null,
    signedUpEmail,
  }),
  phones:
    organisation.phones.length > 0
      ? organisation.phones.map(phone => ({
          main: phone.main,
          type: phone.type,
          number: phone.number,
        }))
      : [
          {
            main: true,
            type: getDefaultOnboardingPhoneType(variant),
            number: "",
          },
        ],
  managingDirector: organisation.managingDirector,
  vatNumber: organisation.vatNumber,
  taxNumber: organisation.taxNumber,
  registrationNumber: organisation.registrationNumber,
  logo: organisation.logo
    ? {
        url: organisation.logo.url,
        title: organisation.logo.title,
        mimeType: organisation.logo.mimeType,
      }
    : null,
  propertyName:
    variant === "property"
      ? getPropertyNamePrefill({ title: organisation.title, ...person })
      : undefined,
});

/** What one website lookup changed, kept for the "Suggested" markers and "Undo". */
interface LookupSession {
  website: string;
  /** The values before the first lookup; "Undo" takes the suggestions back to these. */
  before: OnboardingFormValues;
  /** The values the latest lookup wrote; a suggestion ends when its field differs from these. */
  applied: OnboardingFormValues;
  suggested: SuggestedField[];
  /** What later lookups could still overwrite before this session began. */
  untouchedBefore: OnboardingFormValues | null;
}

/**
 * After a submit that failed validation, brings the first invalid field of the
 * form into view and focuses it. Runs once per submit attempt.
 */
const FocusFirstInvalidField = ({
  form,
}: {
  form: React.RefObject<HTMLFormElement>;
}) => {
  const { submitCount, isSubmitting, errors } =
    useFormikContext<OnboardingFormValues>();
  const handledAttempt = React.useRef(0);
  React.useEffect(() => {
    if (isSubmitting || submitCount === handledAttempt.current) {
      return;
    }
    handledAttempt.current = submitCount;
    if (Object.keys(errors).length > 0) {
      focusFirstInvalidField(form.current);
    }
  }, [isSubmitting, submitCount, errors, form]);
  return null;
};

/** Registers the form's handling of a jump from the rail (see the stage). */
const StepLeave = ({ onLeave }: { onLeave: OnboardingStepLeave }) => {
  useOnboardingStepLeave(onLeave);
  return null;
};

interface Props {
  organisation: OnboardingOrganisation;
  signedUpEmail: string | null;
  setStage: (newStage: OnboardingFlowStage | null) => void;
  /** Called after the details were saved (not on "Skip for now"), before the next stage opens. */
  onSaved?: () => void;
}

/**
 * The form step. A craftsman describes the business ("Your business": website
 * look-up, logo, company and address, managing director, tax and legal
 * details); a property owner only the contact details and the address ("Your
 * property"). Everything is optional on the business step, the owner's contact
 * email is the one required field. Nothing is saved before "Continue", which
 * validates on click: it never looks disabled, and a failed check marks every
 * invalid field and brings the first one into view.
 */
export const OnboardingFlowFormStage = ({
  organisation,
  signedUpEmail,
  setStage,
  onSaved,
}: Props) => {
  const { t } = useTranslate(["Address", "Global", "OrganisationProfile"]);
  const client = useApolloClient();
  const { isLoaded: placesLoaded } = useGoogleMapsApi();
  const [updateOrganisation] =
    useOnboardingFlowModal_UpdateOrganisationMutation({ client });

  const isBusiness = organisation.organisationType === "CRAFTSMAN";
  const variant = isBusiness ? "business" : "property";
  // CRAFTSMAN walks on to the trades; CLIENT (and any future non-CRAFTSMAN
  // type) goes straight to the done step.
  const nextStage: OnboardingFlowStage = isBusiness ? "verticals" : "success";

  const { currentUser } = useUserData();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [prefilling, setPrefilling] = React.useState(false);
  // Whether a website lookup in this visit found a tax or legal value; it
  // opens the tax section, which then stays open for the visit.
  const [lookupFoundTax, setLookupFoundTax] = React.useState(false);
  // A logo found on the website is being imported (see `importPrefilledLogo`).
  const [logoImporting, setLogoImporting] = React.useState(false);
  const logoImportingRef = React.useRef(false);
  // Identifies the active logo import: a newer import, or leaving the step,
  // makes an older one stale, so it can neither write its logo nor clear the
  // loading state of the newer import.
  const logoImportId = React.useRef(0);
  React.useEffect(
    () => () => {
      logoImportId.current += 1;
    },
    []
  );
  const [session, setSession] = React.useState<LookupSession | null>(null);
  // What a website lookup may still overwrite: the form's starting values,
  // updated with every field a lookup has written (see trackPrefilledValues).
  // The step remounts from its starting values on every visit, so this is
  // dropped with it.
  const prefilledRef = React.useRef<OnboardingFormValues | null>(null);

  // Fixed on the first render: later changes of the organisation (the cache
  // refreshes after saving) must not move what "untouched" means.
  const [initialValues] = React.useState(() =>
    buildInitialValues(organisation, signedUpEmail, variant, {
      firstname: currentUser?.firstname,
      familyname: currentUser?.familyname,
    })
  );

  const messages = React.useMemo(
    () => ({
      street: t("Street and number", { ns: "OrganisationProfile" }),
      postalCode: t("Postcode", { ns: "OrganisationProfile" }),
      city: t("City", { ns: "OrganisationProfile" }),
      country: t("Country", { ns: "Global" }),
      email: isBusiness
        ? t("Company email", { ns: "OrganisationProfile" })
        : t("Contact email", { ns: "OrganisationProfile" }),
      invalidPostalCode: t("Invalid postal code", { ns: "Global" }),
      confirmAddress: t("Choose your address from the suggestions", {
        ns: "OrganisationProfile",
      }),
      required: t("Required – please fill in", { ns: "OrganisationProfile" }),
    }),
    [t, isBusiness]
  );
  // The address must be confirmed (Places available) only once the user
  // submits, so that the street field can say what is wrong.
  const submitSchema = React.useMemo(
    () =>
      createOnboardingFormSchema({
        variant,
        requireConfirmedAddress: placesLoaded,
        initialWebsite: initialValues.website,
        messages,
      }),
    [variant, placesLoaded, initialValues.website, messages]
  );

  // Errors show only after a click on Continue. After a failed click they may
  // update live, but only to clear: `shownErrors` holds the field paths shown
  // as invalid, a live validation keeps just those, and each click makes it
  // every current error again. A field that turns invalid later stays quiet
  // until the next click.
  const shownErrors = React.useRef<Set<string>>(new Set());
  const continueClicked = React.useRef(false);
  // Where a jump from the rail goes once the submit it started saved the
  // form; `null` for Continue, which opens the next stage.
  const leaveTarget = React.useRef<OnboardingFlowStage | null>(null);
  const validationRun = React.useRef(0);
  const validate = async (values: OnboardingFormValues) => {
    // Read before awaiting: a click belongs to the validation it starts, not
    // to one that was already running.
    const run = ++validationRun.current;
    const clicked = continueClicked.current;
    continueClicked.current = false;
    let errors = {};
    try {
      await validateYupSchema(values, submitSchema);
    } catch (error) {
      errors = yupToFormErrors(error);
    }
    const paths = getErrorPaths(errors);
    const visible = clicked
      ? paths
      : paths.filter(path => shownErrors.current.has(path));
    // A validation overtaken by a newer one must not move the shown set back.
    if (run === validationRun.current) {
      shownErrors.current = new Set(visible);
    }
    return pickErrors(errors, visible);
  };

  return (
    <Formik<OnboardingFormValues>
      initialValues={initialValues}
      validate={validate}
      onSubmit={async values => {
        // Enter in another field reaches here too: wait for the logo.
        if (logoImportingRef.current) {
          return;
        }
        await updateOrganisation({
          variables: {
            input: buildOrganisationUpdateInput({
              values,
              variant,
              initialLogo: initialValues.logo,
              initialWebsite: initialValues.website,
            }),
          },
        });
        onSaved?.();
        setStage(leaveTarget.current ?? nextStage);
      }}
    >
      {({ values, setValues, setFieldValue, isSubmitting, submitForm }) => {
        // Not awaited by the lookup, so the text fields do not wait for the
        // image. Like the text fields, a found logo replaces whatever the field
        // shows; when nothing usable arrives (failure, timeout, no image) the
        // field stays as it is. Runs detached, so it never rejects.
        const importPrefilledLogo = async (logoUrl: string) => {
          logoImportId.current += 1;
          const importId = logoImportId.current;
          const isActive = () => logoImportId.current === importId;
          logoImportingRef.current = true;
          setLogoImporting(true);
          try {
            const attachment = await importImageFromUrl(logoUrl, {
              timeoutMs: LOGO_IMPORT_TIMEOUT_MS,
            });
            if (isActive() && attachment) {
              await setFieldValue("logo", {
                url: attachment.url,
                title: attachment.title,
                mimeType: attachment.mimeType,
              });
            }
          } catch {
            // Logo prefill fails silently; the current logo stays.
          } finally {
            if (isActive()) {
              logoImportingRef.current = false;
              setLogoImporting(false);
            }
          }
        };

        const suggestions: OnboardingSuggestions | undefined = session
          ? {
              states: getSuggestionStates(
                session.suggested,
                session.applied,
                values
              ),
              before: session.before,
              applied: session.applied,
              onRestore: (field, kind) => {
                void setValues(current =>
                  kind === "input"
                    ? restoreUserInput(current, session.before)
                    : restoreSuggestedField(current, session.applied, field)
                );
              },
            }
          : undefined;
        const result: OnboardingPrefillResult | null = session
          ? { count: session.suggested.length, website: session.website }
          : null;

        return (
          <OnboardingStageLayout
            stage="form"
            organisationType={organisation.organisationType}
            title={
              isBusiness
                ? t("Your business", { ns: "OrganisationProfile" })
                : t("Your property", { ns: "OrganisationProfile" })
            }
            description={
              isBusiness
                ? `${t(
                    "This is printed on every quote, order and invoice you send. Fill it in once, and every document is right.",
                    { ns: "OrganisationProfile" }
                  )} ${t(
                    "Everything on this step is optional – you can complete it later.",
                    { ns: "OrganisationProfile" }
                  )}`
                : t(
                    "Enter it once – so your craftsman can reach you and your projects.",
                    { ns: "OrganisationProfile" }
                  )
            }
            skip={{
              label: t("Skip for now", { ns: "OrganisationProfile" }),
              handleClick: () => setStage(nextStage),
              buttonProps: { id: "skip-onboarding" },
              testId: "skip-onboarding",
            }}
            primary={{
              label: t("Continue", { ns: "Global" }),
              // Never disabled: the click validates and says what is wrong. A
              // save or a website lookup in flight shows as a spinner, which
              // also keeps the click from submitting twice.
              loading: isSubmitting || prefilling || logoImporting,
              handleClick: () => {
                continueClicked.current = true;
              },
              buttonProps: { form: "onboarding-form", type: "submit" },
            }}
          >
            <Form id="onboarding-form" ref={formRef} noValidate>
              <FocusFirstInvalidField form={formRef} />
              {/* A jump from the rail is a Continue towards another step:
                  valid input is saved and the step opens, invalid input
                  keeps the user here with the errors shown. */}
              <StepLeave
                onLeave={target => {
                  if (isSubmitting || prefilling || logoImporting) {
                    return;
                  }
                  leaveTarget.current = target;
                  continueClicked.current = true;
                  // Unlike Formik's handleSubmit (Continue), submitForm
                  // rethrows a failed save; the step then simply stays.
                  void submitForm()
                    .catch(() => undefined)
                    .finally(() => {
                      leaveTarget.current = null;
                    });
                }}
              />
              <Stack spacing="40px">
                {isBusiness && (
                  <OnboardingFlowWebsitePrefill
                    organisationName={organisation.title}
                    initialWebsite={initialValues.website ?? ""}
                    onWebsiteChange={website => {
                      void setFieldValue("website", website || null);
                    }}
                    onLoadingChange={setPrefilling}
                    disabled={logoImporting}
                    result={result}
                    onUndo={async () => {
                      if (!session) {
                        return;
                      }
                      // The restored values count as untouched again, so a
                      // new lookup may fill them.
                      prefilledRef.current = session.untouchedBefore;
                      setSession(null);
                      await setValues(current =>
                        undoPrefill(
                          current,
                          session.before,
                          removeEditedSuggestions(
                            session.suggested,
                            session.applied,
                            current
                          )
                        )
                      );
                    }}
                    onDataFound={async data => {
                      if (lookupFoundTaxValue(data.prefill)) {
                        setLookupFoundTax(true);
                      }
                      if (data.prefill.logoUrl) {
                        void importPrefilledLogo(data.prefill.logoUrl);
                      }
                      const outcome: {
                        current?: {
                          before: OnboardingFormValues;
                          merged: OnboardingFormValues;
                          untouchedBefore: OnboardingFormValues | null;
                        };
                      } = {};
                      await setValues(current => {
                        const untouched = prefilledRef.current ?? initialValues;
                        const merged = mergePrefillIntoFormValues(
                          current,
                          data,
                          untouched
                        );
                        outcome.current = {
                          before: current,
                          merged,
                          untouchedBefore: prefilledRef.current,
                        };
                        prefilledRef.current = trackPrefilledValues(
                          untouched,
                          current,
                          merged
                        );
                        return merged;
                      });
                      const found = outcome.current;
                      if (!found) {
                        return;
                      }
                      const written = getSuggestedFields(
                        found.before,
                        found.merged
                      );
                      setSession(previous => {
                        const standing = previous
                          ? removeEditedSuggestions(
                              previous.suggested,
                              previous.applied,
                              found.before
                            )
                          : [];
                        const all = [...new Set([...standing, ...written])];
                        if (all.length === 0) {
                          return previous;
                        }
                        return {
                          website: data.website,
                          before: previous?.before ?? found.before,
                          applied: found.merged,
                          suggested: all,
                          untouchedBefore: previous
                            ? previous.untouchedBefore
                            : found.untouchedBefore,
                        };
                      });
                    }}
                  />
                )}
                {/* A native disabled fieldset: MUI's FormControl only passes
                    `disabled` through context, which every TextField overrides
                    with its own FormControl. */}
                <Box
                  component="fieldset"
                  disabled={prefilling}
                  sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
                >
                  {isBusiness ? (
                    <OnboardingBusinessFormFields
                      suggestions={suggestions}
                      storedPhoneType={initialValues.phones[0]?.type}
                      showTaxSection={shouldShowTaxSection({
                        lookupFound: lookupFoundTax,
                        initial: initialValues,
                      })}
                      disabled={prefilling}
                      logoImporting={logoImporting}
                    />
                  ) : (
                    <OnboardingPropertyFormFields
                      storedPhoneType={initialValues.phones[0]?.type}
                      disabled={prefilling}
                    />
                  )}
                </Box>
              </Stack>
            </Form>
          </OnboardingStageLayout>
        );
      }}
    </Formik>
  );
};
